// #region Imports
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import type { ReactNode } from "react";
// #endregion

// #region Styled Components
// The flag comes first and the name takes what is left, truncating in it. After the name, the flag
// went wherever the name ended: out of a narrow cell, or under the table's pinned actions column.
// The icon and the text take the issue's colour, and the cell keeps the row's own background (#6097).
const FlaggedName = styled("span", {
  shouldForwardProp: (prop) => prop !== "severity",
})<{ severity: FeeScheduleIssueFlagSeverity }>(({ theme, severity }) => ({
  alignItems: "center",
  boxSizing: "border-box",
  color: theme.palette[severity].dark,
  display: "flex",
  minWidth: 0,
  width: "100%",
  "& .MuiSvgIcon-root": {
    color: theme.palette[severity].main,
    flexShrink: 0,
    fontSize: "1.125rem",
    marginRight: theme.spacing(0.5),
  },
}));
const FlaggedText = styled("span")(() => ({
  display: "block",
  flex: "0 1 auto",
  minWidth: 0,
  overflow: "hidden",
}));
// #endregion

/** What a Team Member cell says of a person the vendor team does not have, where no issue says more. */
export const feeScheduleOffTeamFlagText = "Not on the vendor team";

/** Warning while the proposal is negotiable, error when the problem blocks a hire: the issue's level. */
export type FeeScheduleIssueFlagSeverity = "warning" | "error";

type FeeScheduleIssueFlagProps = {
  children: ReactNode;
  /** What is wrong, in the words of the issue. */
  reason: string;
  severity?: FeeScheduleIssueFlagSeverity;
  /**
   * Which cell this is, for a page that scrolls the grid to it when its issue is pressed (#6097).
   * Drawn as `data-fee-schedule-issue`.
   */
  cellKey?: string;
};

/**
 * One cell of a fee schedule row, marked as the thing that has a problem (#5798, #6097): the Team
 * Member of an hourly rate that names nobody, or somebody off the proposal's vendor team, and the Fee
 * of a rate deferred to the other side and not yet set.
 *
 * Such a rate is flagged rather than changed, because the fix is the user's decision.
 * `WorkAgreement.ValidateFeeSchedule` refuses to hire while either holds, so the grid says so in the
 * cell, and the category editor says what to do. The tooltip carries the same words as the tab's
 * issue, so the cell and the issue list agree.
 */
export default function FeeScheduleIssueFlag(props: Readonly<FeeScheduleIssueFlagProps>) {
  const { reason } = props;
  const severity = props.severity ?? "warning";
  const Icon = severity === "error" ? ErrorOutlinedIcon : WarningAmberIcon;
  return (
    // Described rather than labelled by the tooltip: a label on the cell would replace the name in it.
    <Tooltip title={reason} describeChild>
      <FlaggedName severity={severity} data-severity={severity} data-fee-schedule-issue={props.cellKey}>
        <Icon role="img" aria-label={reason} aria-hidden={false} />
        <FlaggedText>{props.children}</FlaggedText>
      </FlaggedName>
    </Tooltip>
  );
}
